/**
 * Preview-token hook (issue #208) — mints a short-lived signed JWT the
 * SPA appends to the preview URL so the renderer can authenticate the
 * request without an app-host cookie.
 *
 * Why this exists: the admin session cookie is scoped to the app host
 * (`app.<parent>`), but the preview routes live on the public host
 * (`<site>.<parent>` or a custom domain). In the platform's mixed
 * deployment model cookies can't cross to every preview origin, so we
 * mint a per-click JWT and pass it via `?t=<jwt>`. Tokens never reach
 * localStorage / IndexedDB — they're consumed once for the
 * `window.open` call and expire on their own (default 5 minutes).
 *
 * Wire-up at the call site (see `PageDetailPage` archived rows and
 * `ContentItemDetailPage` per-slot Preview buttons):
 *
 * ```ts
 * const createToken = useCreatePreviewToken();
 * const onPreview = async (versionId: string, hostname: string) => {
 *   const { token } = await createToken.mutateAsync({
 *     resourceType: "page-version",
 *     resourceId: versionId,
 *   });
 *   window.open(
 *     buildPublicUrl(hostname, `/preview/page/${versionId}?t=${token}`),
 *     "_blank",
 *     "noopener,noreferrer",
 *   );
 * };
 * ```
 */

import { useMutation } from "@tanstack/react-query";
import type { PreviewTokenRequest, PreviewTokenResponse } from "@ottopilot/shared";
import { apiPost } from "../lib/api";
import type { ApiClientError } from "../lib/api";

/**
 * `POST /api/admin/preview/token` — returns a short-lived signed JWT
 * bound to the (resourceType, resourceId) pair plus an absolute ISO
 * expiry. The caller appends the token to the preview URL and opens
 * the new tab — see the file-header example.
 *
 * No query-key invalidation: minting a token doesn't change any cached
 * server state (tokens are stateless JWTs, never persisted), so there
 * is nothing to invalidate.
 */
export function useCreatePreviewToken() {
  return useMutation<PreviewTokenResponse, ApiClientError, PreviewTokenRequest>({
    mutationFn: (input) => apiPost<PreviewTokenResponse>("/api/admin/preview/token", input),
  });
}
